import React, {FC, useMemo, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {useToggle} from '../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {PaletteColorsEnum} from '../../../../../libs/shared/core/src/models/api/paletteModel';
import {deleteAsync} from '../../../../../libs/shared/core/src/services/http/http';
import {IconTextBox} from '../../../../../libs/shared/ui/src/components/boxedInputs/iconTextBox';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {IconButton} from '../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {OkCancelDialog} from '../../../../../libs/shared/ui/src/components/dialogs/okCancelDialog';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {flexCenter} from '../../../../../libs/shared/ui/src/styles/mixins';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {getCustomEmojiUrl} from '../../../../../srcCommon/helpers/emoji/customEmojiHelpers';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {getCompanyName, getCompanyUrl} from '../../../../store/data/company/companySelectors';
import {
  getCustomEmojiShortcodes,
  getCustomEmojisRoute,
} from '../../../../store/data/customEmojis/customEmojiSelectors';
import {matchEmojiShortcode} from '../../../../store/stateless/emojiSelectors';
import {SyncVirtualScroller} from '../../scroll/syncVirtualScroller';
import {ButtonLoader} from './buttonLoader';

/*
 * Style.
 */

const itemHeight = 52;

const StyledCustomEmojiListRow = styled.div`
  height: ${itemHeight}px;
  font-size: ${fontSizes.medium};
  color: ${(p) => p.theme.greys.shade80};
  font-weight: ${fontWeights.semibold};
  padding: 0 4px;

  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;

  > div {
    ${flexCenter};
  }

  img {
    height: 32px;
    width: 32px;
    margin-right: 12px;
    object-fit: contain;
  }
`;

const StyledCustomEmojiListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const StyledDeleteIcon = styled(IconButton)`
  margin-left: 20px;
  opacity: 0;
  transition: opacity 0.3s ease;

  ${StyledCustomEmojiListRow}:hover & {
    opacity: 1;
  }
`;

const StyledEmojiScroller: typeof SyncVirtualScroller = styled(SyncVirtualScroller)`
  min-height: 200px;
  height: 400px;
`;

/*
 * Component.
 */

export const CustomEmojiList: FC = () => {
  const [emojiToDelete, setEmojiToDelete] = useState<string>();
  const [isDeleting, toggleIsDeleting] = useToggle();
  const [query, setQuery] = useState('');
  const customEmojiShortcodes = useAppSelector(getCustomEmojiShortcodes);
  const sortedShortcodes = useMemo(() => {
    if (query.trim().length) {
      return [...customEmojiShortcodes].filter((e) => matchEmojiShortcode(e, query));
    }

    return [...customEmojiShortcodes].sort();
  }, [customEmojiShortcodes, query]);
  const companyUrl = useAppSelector(getCompanyUrl);
  const customEmojiRoute = useAppSelector(getCustomEmojisRoute);
  const shouldShowDeleteConfirmation = emojiToDelete !== undefined;
  const companyName = useAppSelector(getCompanyName);
  const theme = useUserTheme();

  const onDeleteEmoji = async () => {
    if (!emojiToDelete) {
      return;
    }

    toggleIsDeleting();

    try {
      await deleteAsync(`${companyUrl}/custom_emojis/${emojiToDelete}`);
      setEmojiToDelete(undefined);
    } finally {
      toggleIsDeleting();
    }
  };

  const onCancel = () => setEmojiToDelete(undefined);

  return (
    <>
      <StyledCustomEmojiListWrapper>
        <IconTextBox
          noMargin
          iconName="searchSmall"
          onClear={query.trim().length ? () => setQuery('') : undefined}
          value={query}
          onChange={setQuery}
        />
        <StyledEmojiScroller
          items={sortedShortcodes}
          getItemId={(s) => s}
          itemHeight={itemHeight}
          padding={{bottom: 20}}
          renderItem={(shortcode) => (
            <StyledCustomEmojiListRow>
              <div>
                <img src={getCustomEmojiUrl(customEmojiRoute, shortcode)} alt={`:${shortcode}:`} />
                <span>:{shortcode}:</span>
              </div>
              <StyledDeleteIcon
                color={theme.palette[PaletteColorsEnum.RED].shade50}
                iconName="trashSmall"
                label={<Trans id="custom-emoji-dialog-list-delete-tooltip">Delete</Trans>}
                onClick={() => setEmojiToDelete(shortcode)}
              />
            </StyledCustomEmojiListRow>
          )}
        />
      </StyledCustomEmojiListWrapper>
      {maybeRenderDeleteConfirmation(
        shouldShowDeleteConfirmation,
        isDeleting,
        onDeleteEmoji,
        onCancel,
        companyName,
      )}
    </>
  );
};

function maybeRenderDeleteConfirmation(
  shouldShowDeleteConfirmation: boolean,
  isDeleting: boolean,
  onConfirm: () => void,
  onCancel: () => void,
  companyName: string,
) {
  if (!shouldShowDeleteConfirmation) {
    return null;
  }

  return (
    <OkCancelDialog
      title={<Trans id="custom-emoji-delete-are-you-sure">Delete custom emoji?</Trans>}
      okButtonLabel={isDeleting ? <ButtonLoader /> : <Trans id="custom-emoji-delete-ok">Delete</Trans>}
      cancelButtonLabel={<Trans id="custom-emoji-delete-cancel">Cancel</Trans>}
      onOkClick={onConfirm}
      onRequestClose={onCancel}
      isOkDisabled={isDeleting}
      isCancelDisabled={isDeleting}
      okButtonVariant={ButtonVariantsEnum.DANGER}
    >
      <Trans id="custom-emoji-delete-warning">
        This emoji will be permanently deleted in {companyName}. Action cannot be undone.
      </Trans>
    </OkCancelDialog>
  );
}
